import React, { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ModalAction, Separator } from "../../shared/styles";
import {
  CheckBoxLabel,
  Container,
  CustomModalTitle,
  ModalActionsContainer,
  RadioContainer,
  RadioLabel,
  SelectorsContainer,
  StyledCheckbox,
  StyledRadio,
} from "./styles";
import { messages } from "./messages";
import { FilterDropdown } from "../filterDropdown";
import { AriFilterType, AriType } from "../index";

interface Props {
  applyTo: AriType;
  handleApplyTo: (applyTo: AriType) => void;
  handleChangeFilter: (filter: AriFilterType, value: string) => void;
  handleOnChange: (ticker: string, selected: boolean) => void;
  handleSelectAll: () => void;
  handleUnselectAll: () => void;
  handleSelectAllFilters: (filter: AriFilterType) => void;
  handleUnselectAllFilters: (filter: AriFilterType) => void;
  inventoryLines: {
    ticker: string;
    name: string;
  }[];
  complements?: {
    ticker: string;
  }[];
  extras?: {
    ticker: string;
  }[];
  promos?: {
    ticker: string;
  }[];
  bundles?: {
    ticker: string;
  }[];
  accommodations: { value: string; label: string }[];
  conditions: { value: string; label: string }[];
  mealPlans: { value: string; label: string }[];
  configurations: { value: string; label: string }[];
  selectedItems: string[];
  selectedFilters: {
    accommodations: string[];
    conditions: string[];
    mealPlans: string[];
    occupancies: string[];
  };
}

export const ApplyTo = ({
  applyTo,
  handleApplyTo,
  handleChangeFilter,
  handleOnChange,
  handleSelectAll,
  handleUnselectAll,
  handleSelectAllFilters,
  handleUnselectAllFilters,
  inventoryLines,
  complements,
  extras,
  bundles,
  promos,
  accommodations,
  conditions,
  mealPlans,
  configurations,
  selectedItems,
  selectedFilters,
}: Props): JSX.Element => {
  const intl = useIntl();
  const items: { ticker: string; name?: string }[] | undefined = useMemo(() => {
    switch (applyTo) {
      case AriType.EXTRAS:
        return extras && [...extras];

      case AriType.PROMOS:
        return promos && [...promos];

      case AriType.COMPLEMENTS:
        return complements && [...complements];

      case AriType.BUNDLES:
        return bundles && [...bundles];

      case AriType.INVENTORY_LINES:
      default:
        return [...inventoryLines];
    }
  }, [applyTo, inventoryLines, extras, promos, complements]);

  const isApplicable = useMemo(() =>
    [AriType.INVENTORY_LINES, AriType.BUNDLES].includes(applyTo),
    [applyTo]
  );

  const onChangeApply = useCallback(
    (newApplyTo: AriType) => {
      handleUnselectAll();
      handleApplyTo(newApplyTo);
    },
    [handleUnselectAll, handleApplyTo]
  );

  const onChangeFilter = useCallback(
    (newFilter: AriFilterType, value: string) => {
      handleUnselectAll();
      handleChangeFilter(newFilter, value);
    },
    [handleUnselectAll, handleChangeFilter]
  );

  return (
    <Container>
      <RadioContainer>
        <CustomModalTitle>
          <FormattedMessage {...messages.applyTo} />:
        </CustomModalTitle>

        <RadioLabel>
          <StyledRadio
            checked={applyTo === AriType.INVENTORY_LINES}
            onChange={() => onChangeApply(AriType.INVENTORY_LINES)}
          />
          <FormattedMessage {...messages.inventoryLines} />
        </RadioLabel>

        {!!extras && (
          <RadioLabel>
            <StyledRadio
              checked={applyTo === AriType.EXTRAS}
              onChange={() => onChangeApply(AriType.EXTRAS)}
            />
            <FormattedMessage {...messages.extras} />
          </RadioLabel>
        )}

        {!!promos && (
          <RadioLabel>
            <StyledRadio
              checked={applyTo === AriType.PROMOS}
              onChange={() => onChangeApply(AriType.PROMOS)}
            />
            <FormattedMessage {...messages.promos} />
          </RadioLabel>
        )}

        {!!complements && (
          <RadioLabel>
            <StyledRadio
              checked={applyTo === AriType.COMPLEMENTS}
              onChange={() => onChangeApply(AriType.COMPLEMENTS)}
            />
            <FormattedMessage {...messages.complements} />
          </RadioLabel>
        )}

        {!!bundles && (
          <RadioLabel>
            <StyledRadio
              checked={applyTo === AriType.BUNDLES}
              onChange={() => onChangeApply(AriType.BUNDLES)}
            />
            <FormattedMessage {...messages.bundles} />
          </RadioLabel>
        )}
      </RadioContainer>

      {isApplicable && (
        <>
          <SelectorsContainer>
            <FilterDropdown
              name={AriFilterType.ACCOMMODATION}
              items={accommodations}
              title={intl.formatMessage(messages.allAccommodations)}
              selected={selectedFilters.accommodations}
              onChange={onChangeFilter}
              onSelectAll={() => handleSelectAllFilters(AriFilterType.ACCOMMODATION)}
              onSelectNone={() => handleUnselectAllFilters(AriFilterType.ACCOMMODATION)}
            />
            <FilterDropdown
              name={AriFilterType.CONDITION}
              items={conditions}
              title={intl.formatMessage(messages.allRates)}
              selected={selectedFilters.conditions}
              onChange={onChangeFilter}
              onSelectAll={() => handleSelectAllFilters(AriFilterType.CONDITION)}
              onSelectNone={() => handleUnselectAllFilters(AriFilterType.CONDITION)}
            />
            <FilterDropdown
              name={AriFilterType.MEAL_PLAN}
              items={mealPlans}
              title={intl.formatMessage(messages.allMealplans)}
              selected={selectedFilters.mealPlans}
              onChange={onChangeFilter}
              onSelectAll={() => handleSelectAllFilters(AriFilterType.MEAL_PLAN)}
              onSelectNone={() => handleUnselectAllFilters(AriFilterType.MEAL_PLAN)}
            />
            <FilterDropdown
              name={AriFilterType.CONFIGURATION}
              items={configurations}
              title={intl.formatMessage(messages.allOccupancies)}
              selected={selectedFilters.occupancies}
              onChange={onChangeFilter}
              onSelectAll={() => handleSelectAllFilters(AriFilterType.CONFIGURATION)}
              onSelectNone={() => handleUnselectAllFilters(AriFilterType.CONFIGURATION)}
            />
          </SelectorsContainer>

          <ModalActionsContainer>
            <ModalAction onClick={handleSelectAll}>
              <FormattedMessage {...messages.selectAll} />
            </ModalAction>
            <Separator>|</Separator>
            <ModalAction onClick={handleUnselectAll}>
              <FormattedMessage {...messages.unselectAll} />
            </ModalAction>
          </ModalActionsContainer>
        </>
      )}

      <div>
        {items &&
          Object.values(items)?.map(it => (
            <CheckBoxLabel key={it.ticker}>
              <StyledCheckbox
                checked={selectedItems.includes(it.ticker)}
                onChange={event => handleOnChange(it.ticker, event.target.checked)}
              />
              {it.name || it.ticker}
            </CheckBoxLabel>
          ))}
      </div>
    </Container>
  );
};
